import { useState, useEffect, useContext, useRef } from "react";
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaPrint,
  FaFileCsv,
  FaCalendarAlt,
  FaExchangeAlt,
  FaTrash,
  FaUtensils,
  FaTable,
  FaEye,
  FaSync,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import AuthContext from "../../contexts/AuthContext";
import { getStockMovements } from "../../utils/inventoryApi";

export default function Reports() {
  const { restaurantId } = useContext(AuthContext);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, kitchen, waste
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [viewMode, setViewMode] = useState("table"); // table, cards
  const [showAllItems, setShowAllItems] = useState(false);
  const toastTimeout = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    fetchMovements();
    // eslint-disable-next-line
  }, [restaurantId]);

  async function fetchMovements() {
    setLoading(true);
    setError("");
    try {
      const data = await getStockMovements(restaurantId);
      setMovements(data || []);
    } catch (err) {
      setError("Failed to fetch movements");
      showToast("Failed to fetch movements", "error");
    }
    setLoading(false);
  }

  // Function to refresh data and clear filters
  function handleRefresh() {
    // Clear all filters
    setSearchTerm("");
    setFilterType("all");
    setStartDate("");
    setEndDate("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setShowAllItems(false);

    // Fetch fresh data
    fetchMovements();
  }

  // Filter movements based on criteria
  const filteredMovements = movements.filter((movement) => {
    const matchesSearch =
      movement.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      movement.type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === "all" ||
      (filterType === "kitchen" && movement.toLocation === "KITCHEN") ||
      (filterType === "waste" && movement.toLocation === "WASTE");

    // Handle date filtering properly
    let movementDate = null;
    try {
      if (movement.createdAt) {
        // Handle Firestore Timestamp
        if (
          movement.createdAt.toDate &&
          typeof movement.createdAt.toDate === "function"
        ) {
          movementDate = movement.createdAt.toDate();
        } else if (movement.createdAt._seconds) {
          // Handle serialized Firestore Timestamp (with underscore prefix)
          movementDate = new Date(movement.createdAt._seconds * 1000);
        } else {
          // Handle regular date
          movementDate = new Date(movement.createdAt);
        }

        // Validate the parsed date
        if (isNaN(movementDate.getTime())) {
          movementDate = null;
        }
      }
    } catch (error) {
      console.error("Error parsing movement date:", error);
      movementDate = null;
    }

    const start = startDate ? new Date(startDate + "T00:00:00") : null;
    const end = endDate ? new Date(endDate + "T23:59:59") : null;

    // If no date filters are set, include all movements
    let matchesDate = true;
    if (startDate || endDate) {
      // If date filters are set, only include movements with valid dates
      matchesDate =
        movementDate &&
        (!start || movementDate >= start) &&
        (!end || movementDate <= end);
    }

    return matchesSearch && matchesType && matchesDate;
  });

  // Sort movements
  const sortedMovements = [...filteredMovements].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle date sorting
    if (sortBy === "createdAt") {
      // Parse dates properly for sorting
      let aDate = null;
      let bDate = null;

      try {
        if (a.createdAt) {
          if (a.createdAt.toDate && typeof a.createdAt.toDate === "function") {
            aDate = a.createdAt.toDate();
          } else if (a.createdAt._seconds) {
            aDate = new Date(a.createdAt._seconds * 1000);
          } else {
            aDate = new Date(a.createdAt);
          }
        }

        if (b.createdAt) {
          if (b.createdAt.toDate && typeof b.createdAt.toDate === "function") {
            bDate = b.createdAt.toDate();
          } else if (b.createdAt._seconds) {
            bDate = new Date(b.createdAt._seconds * 1000);
          } else {
            bDate = new Date(b.createdAt);
          }
        }
      } catch (error) {
        // Silently handle date parsing errors for sorting
      }

      aValue = aDate;
      bValue = bDate;
    }

    // Handle null/undefined values
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get items to display (first 10 or all based on showAllItems)
  const itemsToDisplay = showAllItems
    ? sortedMovements
    : sortedMovements.slice(0, 10);
  const hasMoreItems = sortedMovements.length > 10;

  function handlePrint() {
    const printWindow = window.open("", "_blank");
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Inventory Movement Reports</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .filters { margin-bottom: 20px; font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f4f4f4; font-weight: bold; }
            .kitchen { background-color: #e8f5e8; }
            .waste { background-color: #ffe8e8; }
            .summary { margin-top: 20px; padding: 10px; background-color: #f9f9f9; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Inventory Movement Reports</h1>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="filters">
            <strong>Filters:</strong> 
            Type: ${
              filterType === "all"
                ? "All"
                : filterType === "kitchen"
                ? "Kitchen"
                : "Waste"
            } | 
            Date Range: ${startDate || "All"} to ${endDate || "All"} | 
            Search: ${searchTerm || "All"}
          </div>

          <div class="summary">
            <strong>Summary:</strong> 
            Total Movements: ${sortedMovements.length} | 
            Kitchen Transfers: ${
              sortedMovements.filter((m) => m.toLocation === "KITCHEN").length
            } | 
            Waste Records: ${
              sortedMovements.filter((m) => m.toLocation === "WASTE").length
            }
          </div>

          <table>
            <thead>
              <tr>
                                 <th>S.No</th>
                <th>Date</th>
                <th>Item Name</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>From</th>
                <th>To</th>
                <th>Reason</th>
                <th>Reference</th>
                
              </tr>
            </thead>
            <tbody>
              ${sortedMovements
                .map(
                  (movement, index) => `
                <tr class="${
                  movement.toLocation === "KITCHEN" ? "kitchen" : "waste"
                }">
                                     <td>${index + 1}</td>
                   <td>${formatDate(movement.createdAt)}</td>
                  <td>${movement.itemName || "N/A"}</td>
                  <td>${movement.type || "N/A"}</td>
                  <td>${movement.quantity || "N/A"}</td>
                  <td>${movement.fromLocation || "N/A"}</td>
                  <td>${movement.toLocation || "N/A"}</td>
                  <td>${movement.reason || "N/A"}</td>
                  <td>${movement.reference || "N/A"}</td>
                  
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  }

  function handleExportCSV() {
    const headers = [
      "S.No",
      "Date",
      "Item Name",
      "Type",
      "Quantity",
      "From Location",
      "To Location",
      "Reason",
      "Reference",
    ];

    const csvData = sortedMovements.map((movement, index) => [
      index + 1,
      formatDate(movement.createdAt),
      movement.itemName || "",
      movement.type || "",
      movement.quantity || "",
      movement.fromLocation || "",
      movement.toLocation || "",
      movement.reason || "",
      movement.reference || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `inventory_reports_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("CSV file downloaded successfully!", "success");
  }

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(
      () => setToast({ show: false, message: "", type: "" }),
      3000
    );
  }

  function getMovementIcon(movement) {
    if (movement.toLocation === "KITCHEN") {
      return <FaUtensils className="text-green-500" />;
    } else if (movement.toLocation === "WASTE") {
      return <FaTrash className="text-red-500" />;
    }
    return <FaExchangeAlt className="text-blue-500" />;
  }

  function getMovementColor(movement) {
    if (movement.toLocation === "KITCHEN") {
      return "border-l-green-500 bg-green-50 dark:bg-green-900/20";
    } else if (movement.toLocation === "WASTE") {
      return "border-l-red-500 bg-red-50 dark:bg-red-900/20";
    }
    return "border-l-blue-500 bg-blue-50 dark:bg-blue-900/20";
  }

  function formatDate(date) {
    if (!date) return "N/A";

    try {
      let dateObj = null;

      // Handle Firestore Timestamp object with toDate method
      if (date && typeof date.toDate === "function") {
        dateObj = date.toDate();
      }
      // Handle Firestore Timestamp serialized as object with _seconds/_nanoseconds
      else if (
        date &&
        typeof date === "object" &&
        date._seconds !== undefined
      ) {
        dateObj = new Date(date._seconds * 1000);
      }
      // Handle ISO date string
      else if (typeof date === "string") {
        dateObj = new Date(date);
      }
      // Handle regular date object or timestamp
      else {
        dateObj = new Date(date);
      }

      // Validate the date object
      if (!dateObj || isNaN(dateObj.getTime())) {
        return "N/A";
      }

      return dateObj.toLocaleDateString();
    } catch (error) {
      console.error("Date formatting error:", error);
      return "N/A";
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
            Inventory Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            View and analyze inventory movements for kitchen and waste
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handlePrint}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FaPrint /> Print Report
          </button>
          <button
            onClick={handleExportCSV}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FaFileCsv /> Export CSV
          </button>
          <button
            onClick={handleRefresh}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FaSync /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search movements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white"
            >
              <option value="all">All Movements</option>
              <option value="kitchen">Kitchen Transfers</option>
              <option value="waste">Waste Records</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white"
            >
              <option value="createdAt">Date</option>
              <option value="itemName">Item Name</option>
              <option value="quantity">Quantity</option>
              <option value="toLocation">Destination</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Order
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 shadow-lg border border-blue-200 dark:border-blue-700 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                Total Movements
              </p>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-100 mt-2">
                {sortedMovements.length}
              </p>
              <p className="text-xs text-blue-500 dark:text-blue-300 mt-1">
                All inventory movements
              </p>
            </div>
            <div className="bg-blue-500 p-3 rounded-full shadow-lg">
              <FaExchangeAlt className="text-white text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 shadow-lg border border-green-200 dark:border-green-700 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">
                Kitchen Transfers
              </p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-100 mt-2">
                {
                  sortedMovements.filter((m) => m.toLocation === "KITCHEN")
                    .length
                }
              </p>
              <p className="text-xs text-green-500 dark:text-green-300 mt-1">
                Items moved to kitchen
              </p>
            </div>
            <div className="bg-green-500 p-3 rounded-full shadow-lg">
              <FaUtensils className="text-white text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl p-6 shadow-lg border border-red-200 dark:border-red-700 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide">
                Waste Records
              </p>
              <p className="text-3xl font-bold text-red-900 dark:text-red-100 mt-2">
                {sortedMovements.filter((m) => m.toLocation === "WASTE").length}
              </p>
              <p className="text-xs text-red-500 dark:text-red-300 mt-1">
                Items disposed as waste
              </p>
            </div>
            <div className="bg-red-500 p-3 rounded-full shadow-lg">
              <FaTrash className="text-white text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("table")}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              viewMode === "table"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300"
            }`}
          >
            <FaTable /> Table View
          </button>
          <button
            onClick={() => setViewMode("cards")}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              viewMode === "cards"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300"
            }`}
          >
            <FaEye /> Card View
          </button>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {itemsToDisplay.length} of {sortedMovements.length} movements
        </div>
      </div>

      {/* Data Display */}
      {viewMode === "table" ? (
        /* Table View */
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Item Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                {itemsToDisplay.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <FaFilter className="mx-auto text-gray-400 text-4xl mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No movements found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        Try adjusting your filters or search criteria
                      </p>
                    </td>
                  </tr>
                ) : (
                  itemsToDisplay.map((movement, index) => (
                    <tr
                      key={movement.id}
                      className={`hover:bg-gray-50 dark:hover:bg-slate-700 ${
                        movement.toLocation === "KITCHEN"
                          ? "bg-green-50 dark:bg-green-900/10"
                          : movement.toLocation === "WASTE"
                          ? "bg-red-50 dark:bg-red-900/10"
                          : ""
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(movement.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {movement.itemName || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {movement.type || "TRANSFER"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {movement.quantity || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {movement.fromLocation || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            movement.toLocation === "KITCHEN"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : movement.toLocation === "WASTE"
                              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                          }`}
                        >
                          {movement.toLocation || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {movement.reason || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {movement.reference || "N/A"}
                      </td>
                    </tr>
                  ))
                )}
                {hasMoreItems && (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      <button
                        onClick={() => setShowAllItems(!showAllItems)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                      >
                        {showAllItems ? "Read Less" : "Read More"}
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Card View */
        <div className="space-y-4">
          <AnimatePresence>
            {itemsToDisplay.map((movement, index) => (
              <motion.div
                key={movement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-slate-700 border-l-4 ${getMovementColor(
                  movement
                )}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getMovementIcon(movement)}
                      <span className="font-medium text-gray-800 dark:text-white">
                        {movement.type || "TRANSFER"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(movement.createdAt)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {movement.quantity} {movement.itemName}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {movement.fromLocation} → {movement.toLocation}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Item:
                    </span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {movement.itemName || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Reason:
                    </span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {movement.reason || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Reference:
                    </span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {movement.reference || "N/A"}
                    </span>
                  </div>
                </div>

                {movement.notes && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Notes:
                    </span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {movement.notes}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {itemsToDisplay.length === 0 && (
            <div className="text-center py-12">
              <FaFilter className="mx-auto text-gray-400 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No movements found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Try adjusting your filters or search criteria
              </p>
            </div>
          )}
          {hasMoreItems && (
            <div className="text-center py-4">
              <button
                onClick={() => setShowAllItems(!showAllItems)}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
              >
                {showAllItems ? "Read Less" : "Read More"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white shadow-lg z-50 ${
              toast.type === "error" ? "bg-red-500" : "bg-green-500"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
